import { useTheme } from '@fluentui/react';
import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { useEffect, useState } from 'react';
import { HoverEditIcon } from './IconsAndImages/HoverEditIcon';

export interface IPropertySetProps {
  unFocusKey: string;
  readOnly?: boolean;
  focusChildren: JSX.Element | null;
  unFocusChildren: JSX.Element | null;
}

export const PropertySet = (props: IPropertySetProps) => {
  const theme = useTheme();
  const [focus, setFocus] = useState<boolean>(false);
  const [hover, setHover] = useState<boolean>(false);

  useEffect(() => {
    setFocus(false);
    setHover(false);
  }, [props.unFocusKey]);

  if (!props.readOnly && focus) {
    return (
      <IPStack
        styles={{
          root: {
            border: '1px solid grey',
            borderRadius: '3px',
            paddingLeft: '10px',
            paddingRight: '10px',
            paddingBottom: '10px',
            color: '#808080',
          },
        }}
      >
        {props.focusChildren}
      </IPStack>
    );
  } else if (props.readOnly) {
    return (
      <IPStack
        styles={{
          root: {
            paddingLeft: '10px',
            paddingRight: '10px',
            paddingBottom: '5px',
          },
        }}
      >
        <IPStack.Item grow>{props.unFocusChildren}</IPStack.Item>
      </IPStack>
    );
  } else {
    return (
      <IPStack
        tabIndex={0}
        onFocus={(ev) => {
          const element = ev.target as HTMLElement;
          if (element) {
            const className = element.className.toLowerCase();
            if (!(className.indexOf('button') >= 0 || className.indexOf('link') >= 0)) {
              setFocus(true);
            }
          }
        }}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        styles={{
          root: {
            border: `1px solid ${theme.palette.white}`, //invisible border to prevent any resize on hover
            paddingLeft: '10px',
            paddingRight: '10px',
            paddingBottom: '5px',
            '&:hover': {
              cursor: 'text',
              border: '1px solid',
              borderRadius: '3px',
              'border-color': theme.palette.themeSecondary,
              background: theme.palette.neutralLighter,
              transition: 'all 0.3s ease',
            },
          },
        }}
      >
        <IPStack horizontal>
          <IPStack.Item grow>{props.unFocusChildren}</IPStack.Item>
          <IPStack.Item styles={{ root: { minWidth: 40 } }}>
            {hover && <HoverEditIcon left={+15} top={+5} onClick={() => setFocus(true)} />}
          </IPStack.Item>
        </IPStack>
      </IPStack>
    );
  }
};

export interface IPropertySetLabelValueProps {
  label: string;
  value: string | JSX.Element | undefined;
}

export const PropertySetLabelValue = (props: IPropertySetLabelValueProps) => {
  const theme = useTheme();

  const getValue = (): JSX.Element | undefined => {
    if (!props.value || typeof props.value === 'string') {
      return <IPText variant="large">{props.value || '-'}</IPText>;
    } else {
      return props.value;
    }
  };

  return (
    <IPStack
      styles={{
        root: {
          padding: '5px',
        },
      }}
    >
      <IPStack.Item>
        <IPText
          styles={{
            root: { color: theme.palette.neutralSecondary },
          }}
        >
          {props.label}
        </IPText>
      </IPStack.Item>
      <IPStack.Item>{getValue()}</IPStack.Item>
    </IPStack>
  );
};
